import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import {
  getV1MeRetrieveQueryOptions,
  getV1PollsAdminPollsListQueryKey,
  useV1PollsAdminCapabilitiesRetrieve,
  useV1PollsAdminPollsCreate,
  type V1PollsAdminPollsCreateMutationResult,
} from '@/api/client'
import { PageHeader } from '@/components/page-header'
import { pollCapabilitiesOrClosed, toAdminPollWriteRequest } from '@/components/push-poll/poll-api'
import { PollCapabilityGate } from '@/components/push-poll/poll-capability-gate'
import { PollForm } from '@/components/push-poll/poll-form'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

export const Route = createFileRoute('/_panel/zadania/ankiety/nowe')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: NewPollPage,
})

function NewPollPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const capabilitiesQuery = useV1PollsAdminCapabilitiesRetrieve()
  const capabilities = pollCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const createMutation = useV1PollsAdminPollsCreate()
  const [actionError, setActionError] = useState<string>()

  return (
    <>
      <PageHeader title="Nowa ankieta" />
      <div className="p-6">
        <PollCapabilityGate capabilities={capabilities}>
          {actionError && (
            <Alert className="mb-4" variant="destructive">
              <AlertTitle>Nie udało się utworzyć szkicu</AlertTitle>
              <AlertDescription>{actionError}</AlertDescription>
            </Alert>
          )}
          <PollForm
            capabilities={capabilities}
            submitLabel="Utwórz szkic"
            pending={createMutation.isPending}
            onSubmit={async (payload) => {
              setActionError(undefined)
              try {
                const response = await createMutation.mutateAsync({
                  data: toAdminPollWriteRequest(payload),
                })
                if (response.status !== 201) {
                  setActionError(createFailureMessage(response))
                  return false
                }
                await queryClient.invalidateQueries({
                  queryKey: getV1PollsAdminPollsListQueryKey(),
                })
                await navigate({ to: '/zadania/ankiety/$uid', params: { uid: response.data.id } })
              } catch {
                setActionError('Sprawdź połączenie i spróbuj ponownie.')
                return false
              }
            }}
            onCancel={() => void navigate({ to: '/zadania/ankiety' })}
          />
        </PollCapabilityGate>
      </div>
    </>
  )
}

function createFailureMessage(response: V1PollsAdminPollsCreateMutationResult): string {
  switch (response.status) {
    case 400:
    case 403:
    case 404:
      return response.data.message
    default:
      return `Serwer odrzucił zapis (HTTP ${response.status}).`
  }
}
